/**
 * Salesforce-release service — talks to the `salesforce-release` backend via
 * the authenticated backend proxy (`{API_URL}/proxy/release-scanner`).
 *
 * Headers (org_id + user_id + auth) come from `skillsAuthHeaders()` so the
 * logged-in user's session is the source of truth — no hardcoded ids.
 */
import env from "@/config/env";
import { skillsAuthHeaders } from "@/services/skills";

const cleanBaseUrl = (value: string): string => value.replace(/\/+$/, "");

/** e.g. https://ncoder-backend-prod-….run.app/api/proxy/release-scanner */
export const RELEASE_SCANNER_API_BASE_URL = cleanBaseUrl(
  `${cleanBaseUrl(env.API_URL)}/proxy/release-scanner`
);

const BASE = `${RELEASE_SCANNER_API_BASE_URL}/api/v1`;

/** e.g. https://ncoder-backend-prod-….run.app/api/proxy/rag-service (for /api/v2/jobs) */
const JOBS_BASE = cleanBaseUrl(`${cleanBaseUrl(env.API_URL)}/proxy/rag-service`);

// ── Backend response shapes (mirror salesforce-release/app/api/v1/schemas) ──

export type IngestStatus =
  | "pending"
  | "chunking"
  | "embedding"
  | "persisted"
  | "ready"
  | "failed"
  | "cancelled";

export interface ReleaseDocument {
  release_id: string;
  org_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  status: IngestStatus;
  chunk_count: number;
  processed_chunks: number;
  total_chunks: number;
  total_pages: number;
  processed_pages: number;
  file_hash: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Pipeline fields (returned by GET /documents/{id})
  job_id?: string;
  document_id?: string;
  stage?: string;
  job_status?: string;
  progress?: number;
  error?: string | null;
  scan_id?: string | null;
}

export interface DocumentCreatedOut {
  release_id: string;
  stream_url: string;
  deduped?: boolean;
}

export type ImpactBucket = "Breaking" | "Deprecated" | "Review" | "Improvement" | "OK";

export interface BucketCounts {
  breaking: number;
  deprecated: number;
  review: number;
  improvement: number;
  ok: number;
}

export interface EvidenceItem {
  kind: string;
  chunk_id?: string | null;
  chunk_snippet?: string | null;
  section_path?: string | null;
  graph_path?: string[] | null;
  tool_name?: string | null;
  tool_result_summary?: string | null;
  rule_id?: string | null;
  rule_description?: string | null;
  lines?: string | null;
}

export interface FindingOut {
  finding_id: string;
  component_ref: string;
  component_type: string;
  component_name: string;
  bucket: ImpactBucket;
  confidence: "low" | "medium" | "high";
  score: number | null;
  lines: string | null;
  deprecated_or_changed_symbol: string | null;
  replacement: string | null;
  fix_action: string | null;
  rationale: string | null;
  evidence: EvidenceItem[];
  related: string[];
}


// ── Unified pipeline SSE event shapes ─────────────────────────────────

export type PipelineStage =
  | "uploaded"
  | "document_processing"
  | "scan_queued"
  | "scan_starting"
  | "scan_running"
  | "completed"
  | "failed"
  | "cancelled";

export type PipelineEvent =
  | { event: "subscribed"; topic: string }
  | { event: "state"; stage: PipelineStage; status: "queued" | "running" | "completed" | "failed" | "cancelled"; progress: number; release_id: string; error: string | null; is_snapshot?: boolean }
  | { event: "queued"; status: "queued"; elapsed_s: number }
  | { event: "heartbeat"; status?: string; elapsed_s?: number }
  | { event: "ingest_done"; chunk_count: number; release_id: string };

// ── API calls ──────────────────────────────────────────────────────────

async function jsonFetch<T>(
  path: string,
  init: RequestInit = {},
  orgId?: string | null
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...skillsAuthHeaders(orgId),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
  // 204 no content
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// 30 MiB — under Cloud Run's 32 MiB body cap with headroom for form overhead.
// Below this we use the legacy multipart endpoint (one HTTP round-trip).
// Above this we use the 3-step signed-URL flow (avoids the cap, scales to GBs).
const SIGNED_URL_THRESHOLD_BYTES = 30 * 1024 * 1024;

// Shape returned by POST /documents/upload-url. The backend pre-creates the
// release row, so it hands back the release_id used to confirm later.
interface UploadUrlOut {
  release_id: string;
  upload_url: string;
  confirm_url: string;
  expires_in_seconds: number;
}

async function uploadReleaseDocViaSignedUrl(
  file: File,
  orgId: string,
  userId?: string | null
): Promise<DocumentCreatedOut> {
  // 1. Mint a signed PUT URL. The backend reads these as multipart FORM fields
  // (FastAPI Form(...)), NOT a JSON body — sending JSON 422s with input:null.
  const form = new FormData();
  form.append("org_id", orgId);
  form.append("filename", file.name);
  form.append("content_type", file.type || "application/octet-stream");
  if (userId) form.append("uploaded_by", userId);
  // Don't set Content-Type — the browser sets the multipart boundary.
  const signed = await jsonFetch<UploadUrlOut>(
    "/documents/upload-url",
    { method: "POST", body: form },
    orgId
  );

  // 2. PUT the file body directly to GCS. No proxy, no Cloud Run.
  // Content-Type MUST match what we passed to /upload-url, otherwise the
  // signature won't verify (GCS will 403).
  const putRes = await fetch(signed.upload_url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => putRes.statusText);
    throw new Error(`GCS upload failed: ${putRes.status} ${text.slice(0, 200)}`);
  }

  // 3. Confirm — backend reads the GCS object and starts ingest. release_id is
  // in the path; no body. (confirm_url is absolute from the service root, so we
  // rebuild the path relative to BASE to avoid a doubled /api/v1 prefix.)
  return jsonFetch<DocumentCreatedOut>(
    `/documents/${signed.release_id}/confirm-upload`,
    { method: "POST" },
    orgId
  );
}

async function uploadReleaseDocMultipart(
  file: File,
  orgId: string,
  userId?: string | null
): Promise<DocumentCreatedOut> {
  const form = new FormData();
  form.append("org_id", orgId);
  if (userId) form.append("uploaded_by", userId);
  form.append("file", file);
  // Don't set Content-Type — browser does it for multipart.
  return jsonFetch<DocumentCreatedOut>(
    "/documents",
    {
      method: "POST",
      body: form,
    },
    orgId
  );
}

/**
 * Upload a release document. Picks the best path automatically:
 *   - Small files (< 30 MiB): legacy multipart POST /documents.
 *   - Large files (>= 30 MiB): 3-step signed-URL flow — UI gets a v4 signed
 *     URL (+ release_id), PUTs the body direct to GCS (no Cloud Run 32 MiB cap),
 *     then POSTs /documents/{release_id}/confirm-upload which enqueues the Cloud
 *     Run Job that runs the actual chunk → embed → graph-analyse pipeline.
 *
 * The signed-URL path is also taken as a fallback if the multipart upload
 * returns 413 (proxy / GFE size error), so a single retry covers misclassified
 * borderline cases.
 */
export async function uploadReleaseDoc(
  file: File,
  orgId: string,
  userId?: string | null
): Promise<DocumentCreatedOut> {
  if (file.size >= SIGNED_URL_THRESHOLD_BYTES) {
    return uploadReleaseDocViaSignedUrl(file, orgId, userId);
  }
  try {
    return await uploadReleaseDocMultipart(file, orgId, userId);
  } catch (err) {
    const msg = (err as Error)?.message ?? "";
    if (msg.startsWith("413") || msg.includes("Request Entity Too Large")) {
      // Borderline size hit the proxy/GFE cap — fall back to signed URL.
      return uploadReleaseDocViaSignedUrl(file, orgId, userId);
    }
    throw err;
  }
}

export async function listReleaseDocs(
  orgId: string,
  status?: IngestStatus
): Promise<ReleaseDocument[]> {
  const qs = new URLSearchParams({ org_id: orgId });
  if (status) qs.set("status", status);
  const { documents } = await jsonFetch<{ documents: ReleaseDocument[] }>(
    `/documents?${qs.toString()}`,
    { method: "GET" },
    orgId
  );
  return documents;
}

export async function getReleaseDoc(
  releaseId: string,
  orgId?: string | null
): Promise<ReleaseDocument> {
  return jsonFetch<ReleaseDocument>(`/documents/${releaseId}`, { method: "GET" }, orgId);
}

export async function deleteReleaseDoc(
  releaseId: string,
  orgId?: string | null
): Promise<void> {
  await jsonFetch<void>(`/documents/${releaseId}`, { method: "DELETE" }, orgId);
}

export async function reingestReleaseDoc(
  releaseId: string,
  orgId?: string | null
): Promise<DocumentCreatedOut> {
  return jsonFetch<DocumentCreatedOut>(
    `/documents/${releaseId}/reingest`,
    { method: "POST" },
    orgId
  );
}

export async function cancelIngest(
  releaseId: string,
  orgId?: string | null
): Promise<{ status: string }> {
  return jsonFetch<{ status: string }>(
    `/documents/${releaseId}/cancel`,
    { method: "POST" },
    orgId
  );
}


export interface FindingsFilterArgs {
  bucket?: ImpactBucket;
  componentType?: string;
  componentTypes?: string[];
  confidence?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface FindingsAvailableFilters {
  buckets: ImpactBucket[];
  component_types: string[];
  confidences: string[];
}

export interface FindingsSummary {
  total_findings: number;
  bucket_counts: Record<string, number>;
  page: number;
  page_size: number;
  total_pages: number;
  filters_applied: {
    bucket: ImpactBucket | null;
    component_type: string | null;
    component_types: string[] | null;
    confidence: string | null;
    search: string | null;
  };
  available_filters: FindingsAvailableFilters;
}

export interface FindingsResponse {
  status: "ok";
  scan_id: string;
  org_id: string;
  release_id: string;
  scan_status: string;
  summary: FindingsSummary;
  findings: FindingOut[];
}

function appendComponentTypes(qs: URLSearchParams, types?: string[]) {
  if (!types || !types.length) return;
  for (const t of types.slice(0, 10)) qs.append("component_types", t);
}

export async function getFindings(
  orgId: string,
  releaseId: string,
  filters: FindingsFilterArgs = {}
): Promise<FindingsResponse> {
  const qs = new URLSearchParams({ org_id: orgId, release_id: releaseId });
  if (filters.bucket) qs.set("bucket", filters.bucket);
  if (filters.componentType) qs.set("component_type", filters.componentType);
  appendComponentTypes(qs, filters.componentTypes);
  if (filters.confidence) qs.set("confidence", filters.confidence);
  if (filters.search) qs.set("search", filters.search);
  if (filters.page) qs.set("page", String(filters.page));
  if (filters.pageSize) qs.set("page_size", String(filters.pageSize));
  return jsonFetch<FindingsResponse>(`/findings?${qs.toString()}`, { method: "GET" }, orgId);
}

export interface OrgCoverage {
  org_id: string;
  indexed_total: number;
  indexed_by_source: Record<string, number>;  // { code, content, class_summary, name_only }
  org_total_components: number;
  missing_from_index: number;
  last_indexed_at: string | null;
}

export async function getOrgCoverage(orgId: string): Promise<OrgCoverage> {
  const qs = new URLSearchParams({ org_id: orgId });
  return jsonFetch<OrgCoverage>(`/metadata/coverage?${qs.toString()}`, { method: "GET" }, orgId);
}

export async function listOrgMetadataTypes(orgId: string): Promise<string[]> {
  const qs = new URLSearchParams({ org_id: orgId });
  const { types } = await jsonFetch<{ types: string[] }>(
    `/metadata/types?${qs.toString()}`,
    { method: "GET" },
    orgId
  );
  return types;
}

async function blobFetch(
  path: string,
  orgId?: string | null
): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      ...skillsAuthHeaders(orgId),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
  return res.blob();
}

/** Download scan as CSV. Backend enforces org via X-Organization-ID. */
export async function exportScanCsv(
  scanId: string,
  orgId?: string | null,
  filters: Pick<FindingsFilterArgs, "bucket" | "componentType" | "componentTypes"> = {}
): Promise<Blob> {
  const qs = new URLSearchParams();
  if (filters.bucket) qs.set("bucket", filters.bucket);
  if (filters.componentType) qs.set("component_type", filters.componentType);
  appendComponentTypes(qs, filters.componentTypes);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return blobFetch(`/scans/${scanId}/export.csv${suffix}`, orgId);
}

/** Download scan as JSON. */
export async function exportScanJson(
  scanId: string,
  orgId?: string | null,
  filters: Pick<FindingsFilterArgs, "bucket" | "componentType" | "componentTypes"> = {}
): Promise<Blob> {
  const qs = new URLSearchParams();
  if (filters.bucket) qs.set("bucket", filters.bucket);
  if (filters.componentType) qs.set("component_type", filters.componentType);
  appendComponentTypes(qs, filters.componentTypes);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return blobFetch(`/scans/${scanId}/export.json${suffix}`, orgId);
}

// ── Ongoing job detection (for page-refresh reconnection) ─────────────

export interface OngoingReleaseJob {
  job_id: string;       // equals release_id
  status: string;
  stream_url: string;   // e.g. "/api/v1/documents/{id}/events"
  progress: { stage: string; last_heartbeat: string } | null;
}

/**
 * Fetch the currently running release_pipeline job for this org.
 * Used to reconnect the SSE stream after a page refresh.
 * GET /proxy/rag-service/api/v2/jobs?org_id=X&status=running
 */
export async function getOngoingReleaseJob(
  orgId: string,
  userId?: string | null
): Promise<OngoingReleaseJob | null> {
  const qs = new URLSearchParams({ org_id: orgId, status: "running" });
  if (userId) qs.set("user_id", userId);
  try {
    const res = await fetch(`${JOBS_BASE}/api/v2/jobs?${qs.toString()}`, {
      credentials: "include",
      headers: { ...skillsAuthHeaders(orgId) },
    });
    if (!res.ok) return null;
    const body = await res.json();
    const jobs: Array<{
      job_id: string;
      scan_type: string;
      job_type: string;
      status: string;
      stream_url: string;
      progress: { stage: string; last_heartbeat: string } | null;
    }> = body?.data?.jobs ?? body?.jobs ?? [];
    const job = jobs.find((j) => j.scan_type === "release_pipeline" && j.job_id);
    if (!job) return null;
    return {
      job_id: job.job_id,
      status: job.status,
      stream_url: job.stream_url,
      progress: job.progress ?? null,
    };
  } catch {
    return null;
  }
}

// ── SSE streamers ──────────────────────────────────────────────────────

interface StreamHandle {
  abort: () => void;
}

/** Subscribe to the unified pipeline stream for a release document. */
export function streamPipeline(
  releaseId: string,
  handlers: {
    onEvent: (ev: PipelineEvent) => void;
    onError?: (err: unknown) => void;
    onClose?: () => void;
  },
  orgId?: string | null
): StreamHandle {
  return _stream<PipelineEvent>(`/documents/${releaseId}/stream`, handlers, orgId);
}

function _stream<T>(
  path: string,
  handlers: { onEvent: (ev: T) => void; onError?: (err: unknown) => void; onClose?: () => void },
  orgId?: string | null
): StreamHandle {
  const controller = new AbortController();
  void fetch(`${BASE}${path}`, {
    method: "GET",
    credentials: "include",
    signal: controller.signal,
    headers: {
      ...skillsAuthHeaders(orgId),
      Accept: "text/event-stream",
    },
  })
    .then(async (res) => {
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => res.statusText);
        handlers.onError?.(new Error(`stream open failed: ${res.status} ${text.slice(0, 200)}`));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const payload = dataLine.slice(5).trim();
          try {
            const ev = JSON.parse(payload) as T;
            handlers.onEvent(ev);
          } catch {
            /* malformed frame */
          }
        }
      }
      handlers.onClose?.();
    })
    .catch((err) => {
      if ((err as { name?: string })?.name === "AbortError") return;
      handlers.onError?.(err);
    });
  return { abort: () => controller.abort() };
}
